import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield, ShieldCheck, Copy, CheckCircle } from 'lucide-react';
import { useEnrollMFA, useVerifyMFAEnrollment, TOTPEnrollment } from '@/hooks/useMFA';
import { toast } from 'sonner';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';

interface MFAEnrollmentProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function MFAEnrollment({ onSuccess, onCancel }: MFAEnrollmentProps) {
  const [step, setStep] = useState<'intro' | 'qrcode' | 'verify'>('intro');
  const [enrollment, setEnrollment] = useState<TOTPEnrollment | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);

  const enrollMutation = useEnrollMFA();
  const verifyMutation = useVerifyMFAEnrollment();
  const { t } = useTranslation('common');

  const handleStartEnrollment = async () => {
    try {
      const data = await enrollMutation.mutateAsync(undefined);
      setEnrollment(data);
      setStep('qrcode');
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const handleCopySecret = () => {
    if (enrollment?.totp.secret) {
      navigator.clipboard.writeText(enrollment.totp.secret);
      setSecretCopied(true);
      toast.success(t('mfa.secretCopied', 'Secret copied to clipboard'));
      setTimeout(() => setSecretCopied(false), 3000);
    }
  };

  const handleVerify = async () => {
    if (!enrollment || verificationCode.length !== 6) return;

    try {
      await verifyMutation.mutateAsync({
        factorId: enrollment.id,
        code: verificationCode,
      });
      onSuccess?.();
    } catch (_error) {
      // Error handled by mutation
      setVerificationCode('');
    }
  };

  if (step === 'intro') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            {t('mfa.setupTitle', 'Set Up Two-Factor Authentication')}
          </CardTitle>
          <CardDescription>
            {t('mfa.setupDescription', 'Add an extra layer of security to your account by requiring a verification code from your phone.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>{t('mfa.requirement1', '• You will need an authenticator app like Google Authenticator, Authy, or 1Password')}</p>
            <p>{t('mfa.requirement2', '• Each time you sign in, you will need to enter a 6-digit code from your app')}</p>
            <p>{t('mfa.requirement3', '• Make sure to save your backup codes in a secure location')}</p>
          </div>

          <div className="flex gap-2 pt-4">
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                {t('common.cancel', 'Cancel')}
              </Button>
            )}
            <Button onClick={handleStartEnrollment} disabled={enrollMutation.isPending}>
              {enrollMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Shield className="h-4 w-4 mr-2" />
              )}
              {t('mfa.getStarted', 'Get Started')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'qrcode' && enrollment) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('mfa.scanQRCode', 'Scan QR Code')}</CardTitle>
          <CardDescription>
            {t('mfa.scanInstructions', 'Open your authenticator app and scan the QR code below')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-center">
            <div className="p-4 bg-white rounded-lg">
              <img 
                src={enrollment.totp.qr_code} 
                alt={t('common:general.mfaQrCode', 'MFA QR Code')} 
                className="w-48 h-48"
              />
            </div>
          </div>

          <Alert>
            <AlertDescription className="space-y-2">
              <p className="text-sm font-medium">
                {t('mfa.cantScan', "Can't scan? Enter this code manually:")}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono break-all">
                  {enrollment.totp.secret}
                </code>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleCopySecret}
                >
                  {secretCopied ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </AlertDescription>
          </Alert>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={() => setStep('verify')}>
              {t('mfa.next', 'Next')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'verify' && enrollment) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('mfa.verifySetup', 'Verify Setup')}</CardTitle>
          <CardDescription>
            {t('mfa.enterCode', 'Enter the 6-digit code from your authenticator app to verify setup')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>{t('mfa.verificationCode', 'Verification Code')}</Label>
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={verificationCode}
                onChange={setVerificationCode}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep('qrcode')}>
              {t('common.back', 'Back')}
            </Button>
            <Button 
              onClick={handleVerify} 
              disabled={verificationCode.length !== 6 || verifyMutation.isPending}
            >
              {verifyMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <ShieldCheck className="h-4 w-4 mr-2" />
              )}
              {t('mfa.verify', 'Verify & Enable')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
}
